import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  MdSearch, MdRefresh, MdOpenInNew, MdFileDownload, MdFilterList,
  MdClear, MdExpandMore, MdExpandLess, MdAssignment, MdPerson,
  MdGroup, MdBadge, MdArrowUpward, MdArrowDownward,
  MdChevronLeft, MdChevronRight, MdPhoto, MdCheckBox,
  MdCheckBoxOutlineBlank, MdIndeterminateCheckBox, MdImage, MdReplay
} from 'react-icons/md';
import SkeletonRow from '../components/SkeletonRow';
import { helpdeskTicketApi, helpdeskLookupApi, helpdeskCategoryApi, helpdeskSubCategoryApi } from '../../../api/helpdeskApi';
import { toast } from 'react-toastify';
import '../Admin.css';
import './HelpdeskReports.css';

const STATUS_LABELS = {
  OPEN: 'Open', ASSIGNED: 'Assigned', IN_PROGRESS: 'In Progress',
  RESOLVED: 'Resolved', CLOSED: 'Closed', REOPENED: 'Reopened'
};
const STATUS_OPTIONS = Object.keys(STATUS_LABELS);
const PRIORITY_OPTIONS = ['LOW', 'MEDIUM', 'HIGH', 'CRITICAL'];
const ID_CARD_STATUS_OPTIONS = ['SUBMITTED', 'DATA_VERIFIED', 'PHOTO_APPROVED', 'PRINT_QUEUE', 'PRINTING', 'PRINT_READY', 'DELIVERED', 'REJECTED'];
const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

const REPORT_TABS = [
  { key: 'my-tickets', label: 'My Tickets', icon: MdPerson, description: 'Tickets created by you' },
  { key: 'assigned-to-me', label: 'My Reports', icon: MdAssignment, description: 'Tickets assigned to you' },
  { key: 'team', label: 'Team Reports', icon: MdGroup, description: 'All team tickets' },
  { key: 'id-card', label: 'ID Card Reports', icon: MdBadge, description: 'All ID card requests' },
];

const MY_TICKETS_COLUMNS = [
  { key: 'index', label: 'S.No', sortable: false },
  { key: 'action', label: '', sortable: false },
  { key: 'ticketNumber', label: 'Ticket #', sortable: true },
  { key: 'createdAt', label: 'Date', sortable: true },
  { key: 'title', label: 'Title', sortable: true },
  { key: 'requestType', label: 'Type', sortable: true },
  { key: 'category', label: 'Category', sortable: true },
  { key: 'priority', label: 'Priority', sortable: true },
  { key: 'ticketStatus', label: 'Status', sortable: true },
  { key: 'idCardStatus', label: 'ID Card Status', sortable: true },
  { key: 'assignedTo', label: 'Assigned To', sortable: true },
];

const ASSIGNED_COLUMNS = [
  { key: 'index', label: 'S.No', sortable: false },
  { key: 'action', label: '', sortable: false },
  { key: 'ticketNumber', label: 'Ticket #', sortable: true },
  { key: 'createdAt', label: 'Date', sortable: true },
  { key: 'title', label: 'Title', sortable: true },
  { key: 'requestType', label: 'Type', sortable: true },
  { key: 'category', label: 'Category', sortable: true },
  { key: 'priority', label: 'Priority', sortable: true },
  { key: 'city', label: 'City', sortable: true },
  { key: 'requester', label: 'Requested By', sortable: true },
  { key: 'ticketStatus', label: 'Status', sortable: true },
  { key: 'idCardStatus', label: 'ID Card Status', sortable: true },
];

const TEAM_COLUMNS = [
  { key: 'index', label: 'S.No', sortable: false },
  { key: 'action', label: '', sortable: false },
  { key: 'ticketNumber', label: 'Ticket #', sortable: true },
  { key: 'createdAt', label: 'Date', sortable: true },
  { key: 'title', label: 'Title', sortable: true },
  { key: 'requestType', label: 'Type', sortable: true },
  { key: 'category', label: 'Category', sortable: true },
  { key: 'priority', label: 'Priority', sortable: true },
  { key: 'city', label: 'City', sortable: true },
  { key: 'workLocation', label: 'Location', sortable: true },
  { key: 'requester', label: 'Requested By', sortable: true },
  { key: 'assignedTo', label: 'Assigned To', sortable: true },
  { key: 'ticketStatus', label: 'Status', sortable: true },
  { key: 'idCardStatus', label: 'ID Card Status', sortable: true },
];

const IDCARD_COLUMNS = [
  { key: 'index', label: 'S.No', sortable: false },
  { key: 'action', label: '', sortable: false },
  { key: 'employeeId', label: 'Employee ID', sortable: true },
  { key: 'employeeName', label: 'Employee Name', sortable: true },
  { key: 'employeeLocation', label: 'Location', sortable: true },
  { key: 'workLocation', label: 'Work Location', sortable: true },
  { key: 'bloodGroup', label: 'Blood Group', sortable: true },
  { key: 'contactNumber', label: 'Contact No.', sortable: true },
  { key: 'emergencyContact', label: 'Emergency Contact', sortable: true },
  { key: 'idCardStatus', label: 'ID Card Status', sortable: true },
  { key: 'ticketStatus', label: 'Ticket Status', sortable: true },
  { key: 'hasPhoto', label: 'Id Photo', sortable: false },
  { key: 'employeeDoj', label: 'DOJ', sortable: true },
  { key: 'residentialAddress', label: 'Address', sortable: true },
  { key: 'createdAt', label: 'Created On', sortable: true },
];

function getColumnsForTab(tab) {
  switch (tab) {
    case 'my-tickets': return MY_TICKETS_COLUMNS;
    case 'assigned-to-me': return ASSIGNED_COLUMNS;
    case 'team': return TEAM_COLUMNS;
    case 'id-card': return IDCARD_COLUMNS;
    default: return TEAM_COLUMNS;
  }
}

function HelpdeskReports() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('my-tickets');
  const [tickets, setTickets] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  // Filters
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [priorityFilter, setPriorityFilter] = useState('ALL');
  const [locationFilter, setLocationFilter] = useState('ALL');
  const [idCardStatusFilter, setIdCardStatusFilter] = useState('ALL');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  // Lookups
  const [locations, setLocations] = useState([]);

  // Sorting
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortDir, setSortDir] = useState('desc');

  // Pagination
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  // Selection & Bulk Actions
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [bulkStatus, setBulkStatus] = useState('');
  const [bulkComment, setBulkComment] = useState('');
  const [bulkUpdating, setBulkUpdating] = useState(false);
  const [exportingImages, setExportingImages] = useState(false);
  const [showReprocessModal, setShowReprocessModal] = useState(false);
  const [reprocessComment, setReprocessComment] = useState('');
  const [reprocessing, setReprocessing] = useState(false);

  // Advanced filters (my-tickets, assigned-to-me, team tabs)
  const [categoryFilter, setCategoryFilter] = useState('ALL');
  const [subCategoryFilter, setSubCategoryFilter] = useState('ALL');
  const [cityFilter, setCityFilter] = useState('ALL');
  const [assigneeFilter, setAssigneeFilter] = useState('ALL');
  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [filtersApplied, setFiltersApplied] = useState(false);

  // Load locations and categories
  useEffect(() => {
    helpdeskLookupApi.getCities().then(setLocations).catch(() => {});
    helpdeskCategoryApi.getActive().then(setCategories).catch(() => {});
  }, []);

  // Load subcategories when category filter changes
  useEffect(() => {
    if (categoryFilter && categoryFilter !== 'ALL') {
      const cat = categories.find(c => c.categoryName === categoryFilter);
      if (cat) {
        helpdeskSubCategoryApi.getByCategory(cat.id).then(setSubCategories).catch(() => setSubCategories([]));
      }
    } else {
      setSubCategories([]);
      setSubCategoryFilter('ALL');
    }
  }, [categoryFilter, categories]);

  const loadReport = useCallback(async (tab) => {
    setIsLoading(true);
    try {
      let data;
      switch (tab) {
        case 'my-tickets':
          data = await helpdeskTicketApi.getMyTicketsReport();
          break;
        case 'assigned-to-me':
          data = await helpdeskTicketApi.getAssignedToMeReport();
          break;
        case 'team':
          data = await helpdeskTicketApi.getTeamReport();
          break;
        case 'id-card':
          data = await helpdeskTicketApi.getIdCardReport();
          break;
        default:
          data = [];
      }
      setTickets(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error('Failed to load report:', e);
      toast.error('Failed to load report data');
      setTickets([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadReport(activeTab);
  }, [activeTab, loadReport]);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setSearchQuery('');
    setStatusFilter('ALL');
    setPriorityFilter('ALL');
    setLocationFilter('ALL');
    setIdCardStatusFilter('ALL');
    setDateFrom('');
    setDateTo('');
    setSortBy('createdAt');
    setSortDir('desc');
    setCurrentPage(0);
    setSelectedIds(new Set());
    // Reset advanced filters
    setCategoryFilter('ALL');
    setSubCategoryFilter('ALL');
    setCityFilter('ALL');
    setAssigneeFilter('ALL');
    setFiltersApplied(false);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-IN', {
      timeZone: 'Asia/Kolkata', day: '2-digit', month: '2-digit', year: 'numeric'
    });
  };

  const isFilterPanelTab = activeTab !== 'id-card';
  const hasActiveFilters = statusFilter !== 'ALL' || priorityFilter !== 'ALL' || locationFilter !== 'ALL' || idCardStatusFilter !== 'ALL' || dateFrom || dateTo ||
    categoryFilter !== 'ALL' || subCategoryFilter !== 'ALL' || cityFilter !== 'ALL' || assigneeFilter !== 'ALL';
  const activeFilterCount = [statusFilter !== 'ALL', priorityFilter !== 'ALL', locationFilter !== 'ALL', idCardStatusFilter !== 'ALL', !!dateFrom, !!dateTo,
    categoryFilter !== 'ALL', subCategoryFilter !== 'ALL', cityFilter !== 'ALL', assigneeFilter !== 'ALL'].filter(Boolean).length;

  const clearFilters = () => {
    setStatusFilter('ALL');
    setPriorityFilter('ALL');
    setLocationFilter('ALL');
    setIdCardStatusFilter('ALL');
    setDateFrom('');
    setDateTo('');
    setCategoryFilter('ALL');
    setSubCategoryFilter('ALL');
    setCityFilter('ALL');
    setAssigneeFilter('ALL');
    setFiltersApplied(false);
    setCurrentPage(0);
  };

  const handleFilterDisplay = () => {
    setFiltersApplied(true);
    setCurrentPage(0);
  };

  const clearAdvancedFilters = () => {
    setDateFrom('');
    setDateTo('');
    setStatusFilter('ALL');
    setPriorityFilter('ALL');
    setIdCardStatusFilter('ALL');
    setCategoryFilter('ALL');
    setSubCategoryFilter('ALL');
    setCityFilter('ALL');
    setAssigneeFilter('ALL');
    setFiltersApplied(false);
    setCurrentPage(0);
  };

  // Unique locations from tickets for ID card filter
  const ticketLocations = useMemo(() => {
    if (activeTab !== 'id-card') return [];
    const locs = new Set();
    tickets.forEach(t => {
      if (t.employeeLocation) locs.add(t.employeeLocation);
    });
    return [...locs].sort();
  }, [tickets, activeTab]);

  // Unique values from tickets for filter dropdowns (non-ID-card tabs)
  const filterOptions = useMemo(() => {
    if (activeTab === 'id-card') return { cities: [], assignees: [], requesters: [] };
    const cities = new Set();
    const assignees = new Map();
    const requesters = new Map();
    tickets.forEach(t => {
      if (t.city) cities.add(t.city);
      if (t.assignedTo) {
        const name = `${t.assignedTo.firstName} ${t.assignedTo.secondName}`;
        assignees.set(name, name);
      }
      if (t.requester) {
        const name = `${t.requester.firstName} ${t.requester.secondName}`;
        requesters.set(name, name);
      }
    });
    return {
      cities: [...cities].sort(),
      assignees: [...assignees.values()].sort(),
      requesters: [...requesters.values()].sort(),
    };
  }, [tickets, activeTab]);

  // Client-side filtering + sorting
  const filteredTickets = useMemo(() => {
    let result = [...tickets];

    // Search
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      result = result.filter(t =>
        t.ticketNumber?.toLowerCase().includes(q) ||
        t.title?.toLowerCase().includes(q) ||
        t.employeeName?.toLowerCase().includes(q) ||
        t.employeeId?.toLowerCase().includes(q) ||
        t.category?.categoryName?.toLowerCase().includes(q) ||
        t.requester?.firstName?.toLowerCase().includes(q) ||
        t.requester?.secondName?.toLowerCase().includes(q) ||
        t.city?.toLowerCase().includes(q) ||
        t.employeeLocation?.toLowerCase().includes(q) ||
        t.residentialAddress?.toLowerCase().includes(q) ||
        t.contactNumber?.toLowerCase().includes(q)
      );
    }

    // Status filter
    if (statusFilter !== 'ALL') {
      result = result.filter(t => t.ticketStatus === statusFilter);
    }

    // Priority filter
    if (priorityFilter !== 'ALL') {
      result = result.filter(t => t.priority === priorityFilter);
    }

    // ID Card Status filter
    if (idCardStatusFilter !== 'ALL') {
      result = result.filter(t => t.idCardStatus === idCardStatusFilter);
    }

    // Location filter (for ID card tab)
    if (locationFilter !== 'ALL') {
      result = result.filter(t => t.employeeLocation === locationFilter);
    }

    // Advanced filters (non-ID-card tabs, applied via Display button)
    if (activeTab !== 'id-card' && filtersApplied) {
      if (categoryFilter !== 'ALL') {
        result = result.filter(t => t.category?.categoryName === categoryFilter);
      }
      if (subCategoryFilter !== 'ALL') {
        result = result.filter(t => t.subCategory?.subCategoryName === subCategoryFilter);
      }
      if (cityFilter !== 'ALL') {
        result = result.filter(t => t.city === cityFilter);
      }
      if (assigneeFilter !== 'ALL') {
        if (activeTab === 'assigned-to-me') {
          // For "My Reports" tab, assigneeFilter filters by requester
          result = result.filter(t => {
            const requesterName = t.requester ? `${t.requester.firstName} ${t.requester.secondName}` : '';
            return requesterName === assigneeFilter;
          });
        } else {
          // For other tabs, assigneeFilter filters by assigned to
          result = result.filter(t => {
            const assigneeName = t.assignedTo ? `${t.assignedTo.firstName} ${t.assignedTo.secondName}` : '';
            return assigneeName === assigneeFilter;
          });
        }
      }
    }

    // Date filters
    if (dateFrom) {
      const from = new Date(dateFrom);
      result = result.filter(t => t.createdAt && new Date(t.createdAt) >= from);
    }
    if (dateTo) {
      const to = new Date(dateTo + 'T23:59:59');
      result = result.filter(t => t.createdAt && new Date(t.createdAt) <= to);
    }

    // Sorting
    result.sort((a, b) => {
      let valA, valB;
      switch (sortBy) {
        case 'ticketNumber': valA = a.ticketNumber || ''; valB = b.ticketNumber || ''; break;
        case 'createdAt': valA = a.createdAt || ''; valB = b.createdAt || ''; break;
        case 'title': valA = (a.title || '').toLowerCase(); valB = (b.title || '').toLowerCase(); break;
        case 'requestType': valA = a.requestType || ''; valB = b.requestType || ''; break;
        case 'category': valA = a.category?.categoryName || ''; valB = b.category?.categoryName || ''; break;
        case 'priority': {
          const order = { CRITICAL: 4, HIGH: 3, MEDIUM: 2, LOW: 1 };
          valA = order[a.priority] || 0; valB = order[b.priority] || 0; break;
        }
        case 'city': valA = a.city || ''; valB = b.city || ''; break;
        case 'requester': valA = a.requester ? `${a.requester.firstName} ${a.requester.secondName}` : ''; valB = b.requester ? `${b.requester.firstName} ${b.requester.secondName}` : ''; break;
        case 'assignedTo': valA = a.assignedTo ? `${a.assignedTo.firstName} ${a.assignedTo.secondName}` : ''; valB = b.assignedTo ? `${b.assignedTo.firstName} ${b.assignedTo.secondName}` : ''; break;
        case 'ticketStatus': valA = a.ticketStatus || ''; valB = b.ticketStatus || ''; break;
        case 'idCardStatus': valA = a.idCardStatus || ''; valB = b.idCardStatus || ''; break;
        case 'employeeId': valA = a.employeeId || ''; valB = b.employeeId || ''; break;
        case 'employeeName': valA = (a.employeeName || '').toLowerCase(); valB = (b.employeeName || '').toLowerCase(); break;
        case 'employeeLocation': valA = (a.employeeLocation || '').toLowerCase(); valB = (b.employeeLocation || '').toLowerCase(); break;
        case 'workLocation': valA = (a.workLocation?.locationName || a.employeeLocation || '').toLowerCase(); valB = (b.workLocation?.locationName || b.employeeLocation || '').toLowerCase(); break;
        case 'bloodGroup': valA = a.bloodGroup || ''; valB = b.bloodGroup || ''; break;
        case 'contactNumber': valA = a.contactNumber || ''; valB = b.contactNumber || ''; break;
        case 'emergencyContact': valA = a.emergencyContact || ''; valB = b.emergencyContact || ''; break;
        case 'employeeDoj': valA = a.employeeDoj || ''; valB = b.employeeDoj || ''; break;
        case 'residentialAddress': valA = (a.residentialAddress || '').toLowerCase(); valB = (b.residentialAddress || '').toLowerCase(); break;
        default: valA = a.createdAt || ''; valB = b.createdAt || '';
      }
      if (valA < valB) return sortDir === 'asc' ? -1 : 1;
      if (valA > valB) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });

    return result;
  }, [tickets, searchQuery, statusFilter, priorityFilter, idCardStatusFilter, locationFilter, dateFrom, dateTo, sortBy, sortDir, activeTab, categoryFilter, subCategoryFilter, cityFilter, assigneeFilter, filtersApplied]);

  // Pagination
  const totalPages = Math.max(1, Math.ceil(filteredTickets.length / pageSize));
  const paginatedTickets = useMemo(() => {
    const start = currentPage * pageSize;
    return filteredTickets.slice(start, start + pageSize);
  }, [filteredTickets, currentPage, pageSize]);

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(0);
  }, [searchQuery, statusFilter, priorityFilter, idCardStatusFilter, locationFilter, dateFrom, dateTo]);

  // Status summary
  const statusSummary = useMemo(() => {
    const counts = {};
    tickets.forEach(t => {
      const s = t.ticketStatus || 'UNKNOWN';
      counts[s] = (counts[s] || 0) + 1;
    });
    return counts;
  }, [tickets]);

  const handleSort = (columnKey) => {
    if (sortBy === columnKey) {
      setSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(columnKey);
      setSortDir('asc');
    }
    setCurrentPage(0);
  };

  const renderSortIcon = (columnKey) => {
    if (sortBy !== columnKey) return <span className="sort-icon sort-inactive">&#8693;</span>;
    return sortDir === 'asc'
      ? <MdArrowUpward className="sort-icon sort-active" />
      : <MdArrowDownward className="sort-icon sort-active" />;
  };

  const exportToCSV = () => {
    if (filteredTickets.length === 0) {
      toast.info('No data to export');
      return;
    }
    const columns = getColumnsForTab(activeTab).filter(c => c.key !== 'index' && c.key !== 'action' && c.key !== 'hasPhoto');
    const headers = columns.map(c => c.label);
    const rows = filteredTickets.map(t => columns.map(col => {
      const val = getCellText(t, col.key);
      return (val || '').replace(/,/g, ' ').replace(/"/g, "'");
    }));

    const csv = [headers.join(','), ...rows.map(r => r.map(c => `"${c}"`).join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const tabLabel = REPORT_TABS.find(t => t.key === activeTab)?.label || 'report';
    a.download = `helpdesk-${tabLabel.replace(/\s+/g, '-').toLowerCase()}-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${filteredTickets.length} records`);
  };

  // Selection handlers
  const toggleSelectAll = () => {
    if (selectedIds.size === paginatedTickets.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(paginatedTickets.map(t => t.id)));
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const allPageSelected = paginatedTickets.length > 0 && paginatedTickets.every(t => selectedIds.has(t.id));
  const somePageSelected = paginatedTickets.some(t => selectedIds.has(t.id));

  // Bulk status update
  const handleBulkUpdate = async () => {
    if (!bulkStatus || selectedIds.size === 0) return;
    setBulkUpdating(true);
    try {
      const result = await helpdeskTicketApi.bulkUpdateStatus(
        Array.from(selectedIds),
        bulkStatus,
        bulkComment || undefined
      );
      toast.success(`Updated ${result.updated || 0} of ${result.total || selectedIds.size} tickets`);
      if (result.failed > 0) {
        toast.warning(`${result.failed} ticket(s) failed to update`);
      }
      setShowBulkModal(false);
      setBulkStatus('');
      setBulkComment('');
      setSelectedIds(new Set());
      loadReport(activeTab);
    } catch (e) {
      console.error('Bulk update failed:', e);
      toast.error('Bulk update failed');
    } finally {
      setBulkUpdating(false);
    }
  };

  // Export to Excel + images ZIP (ID Card tab)
  const exportWithImages = async () => {
    const ticketsToExport = selectedIds.size > 0
      ? filteredTickets.filter(t => selectedIds.has(t.id))
      : filteredTickets;

    if (ticketsToExport.length === 0) {
      toast.info('No data to export');
      return;
    }

    setExportingImages(true);
    try {
      const [ExcelJS, JSZip] = await Promise.all([
        import('exceljs').then(m => m.default),
        import('jszip').then(m => m.default),
      ]);

      const zip = new JSZip();
      const imagesFolder = zip.folder('images');
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('ID Card Report');

      // Define columns
      worksheet.columns = [
        { header: 'S.No', key: 'sno', width: 6 },
        { header: 'Employee ID', key: 'employeeId', width: 15 },
        { header: 'Employee Name', key: 'employeeName', width: 22 },
        { header: 'Location', key: 'employeeLocation', width: 18 },
        { header: 'Blood Group', key: 'bloodGroup', width: 12 },
        { header: 'Contact No.', key: 'contactNumber', width: 15 },
        { header: 'Emergency Contact', key: 'emergencyContact', width: 18 },
        { header: 'Status', key: 'status', width: 14 },
        { header: 'Photo File', key: 'photoFile', width: 25 },
        { header: 'DOJ', key: 'doj', width: 12 },
        { header: 'Address', key: 'address', width: 30 },
        { header: 'Created On', key: 'createdOn', width: 14 },
      ];

      // Style header row
      const headerRow = worksheet.getRow(1);
      headerRow.font = { bold: true, color: { argb: 'FFFFFFFF' } };
      headerRow.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF704493' } };
      headerRow.alignment = { vertical: 'middle', horizontal: 'center' };
      headerRow.height = 25;

      let photoCount = 0;

      // Add data rows and download photos
      for (let i = 0; i < ticketsToExport.length; i++) {
        const t = ticketsToExport[i];
        const empId = t.employeeId || `ticket-${t.ticketNumber || i + 1}`;
        let photoFileName = '';

        // Download photo and add to ZIP
        if (t.photoName) {
          try {
            const response = await helpdeskTicketApi.downloadPhoto(t.id);
            const blob = response.data;
            const ext = (t.photoName || '').split('.').pop()?.toLowerCase() || 'jpg';
            photoFileName = `${empId}.${ext}`;
            imagesFolder.file(photoFileName, blob);
            photoCount++;
          } catch {
            // Photo download failed
          }
        }

        worksheet.addRow({
          sno: i + 1,
          employeeId: t.employeeId || '',
          employeeName: t.employeeName || '',
          employeeLocation: t.employeeLocation || '',
          bloodGroup: t.bloodGroup || '',
          contactNumber: t.contactNumber || '',
          emergencyContact: t.emergencyContact || '',
          status: STATUS_LABELS[t.ticketStatus] || t.ticketStatus || '',
          photoFile: photoFileName || 'No photo',
          doj: t.employeeDoj || '',
          address: t.residentialAddress || '',
          createdOn: formatDate(t.createdAt),
        });
      }

      // Add Excel file to ZIP
      const xlsxBuffer = await workbook.xlsx.writeBuffer();
      zip.file('ID-Card-Report.xlsx', xlsxBuffer);

      // Generate ZIP and download
      const zipBlob = await zip.generateAsync({ type: 'blob' });
      const url = URL.createObjectURL(zipBlob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `id-card-report-${new Date().toISOString().split('T')[0]}.zip`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${ticketsToExport.length} records + ${photoCount} images as ZIP`);
    } catch (e) {
      console.error('Export with images failed:', e);
      toast.error('Export failed. Please try CSV export instead.');
    } finally {
      setExportingImages(false);
    }
  };

  // Reprocess — request photo re-upload from employee
  const handleReprocess = async () => {
    if (!reprocessComment.trim() || selectedIds.size === 0) return;
    setReprocessing(true);
    try {
      const result = await helpdeskTicketApi.reprocessTickets(
        Array.from(selectedIds),
        reprocessComment
      );
      toast.success(`Reprocessed ${result.processed || 0} of ${result.total || selectedIds.size} tickets — email sent to employees`);
      if (result.failed > 0) {
        toast.warning(`${result.failed} ticket(s) failed to reprocess`);
      }
      setShowReprocessModal(false);
      setReprocessComment('');
      setSelectedIds(new Set());
      loadReport(activeTab);
    } catch (e) {
      console.error('Reprocess failed:', e);
      toast.error('Reprocess failed');
    } finally {
      setReprocessing(false);
    }
  };

  const getCellText = (ticket, columnKey) => {
    switch (columnKey) {
      case 'ticketNumber': return ticket.ticketNumber || '';
      case 'createdAt': return formatDate(ticket.createdAt);
      case 'title': return ticket.title || '';
      case 'requestType': return ticket.requestType === 'GENERAL_REQUEST' ? 'General' : 'ID Card';
      case 'category': return ticket.category?.categoryName || '';
      case 'priority': return ticket.priority || '';
      case 'city': return ticket.city || '';
      case 'requester': return ticket.requester ? `${ticket.requester.firstName} ${ticket.requester.secondName}` : '';
      case 'assignedTo': return ticket.assignedTo ? `${ticket.assignedTo.firstName} ${ticket.assignedTo.secondName}` : 'Unassigned';
      case 'ticketStatus': return STATUS_LABELS[ticket.ticketStatus] || ticket.ticketStatus?.replace('_', ' ') || '';
      case 'idCardStatus': return ticket.idCardStatus?.replace('_', ' ') || '';
      case 'employeeId': return ticket.employeeId || '';
      case 'employeeName': return ticket.employeeName || '';
      case 'employeeLocation': return ticket.employeeLocation || '';
      case 'workLocation': return ticket.workLocation?.locationName || ticket.employeeLocation || '';
      case 'bloodGroup': return ticket.bloodGroup || '';
      case 'contactNumber': return ticket.contactNumber || '';
      case 'emergencyContact': return ticket.emergencyContact || '';
      case 'employeeDoj': return ticket.employeeDoj || '';
      case 'residentialAddress': return ticket.residentialAddress || '';
      default: return '';
    }
  };

  const renderCellValue = (ticket, columnKey) => {
    switch (columnKey) {
      case 'ticketNumber':
        return <span className="ticket-number">{ticket.ticketNumber}</span>;
      case 'createdAt':
        return <span className="date-cell">{formatDate(ticket.createdAt)}</span>;
      case 'title':
        return <span className="ticket-title">{ticket.title || '-'}</span>;
      case 'requestType':
        return (
          <span className={`type-badge ${ticket.requestType === 'ID_CARD_REQUEST' ? 'type-idcard' : ''}`}>
            {ticket.requestType === 'GENERAL_REQUEST' ? 'General' : 'ID Card'}
          </span>
        );
      case 'category':
        return ticket.category?.categoryName || '-';
      case 'priority':
        return ticket.priority ? (
          <span className={`priority-badge priority-${ticket.priority.toLowerCase()}`}>{ticket.priority}</span>
        ) : '-';
      case 'city':
        return ticket.city || '-';
      case 'workLocation':
        return ticket.workLocation?.locationName || '-';
      case 'requester':
        return ticket.requester ? `${ticket.requester.firstName} ${ticket.requester.secondName}` : '-';
      case 'assignedTo':
        return ticket.assignedTo
          ? `${ticket.assignedTo.firstName} ${ticket.assignedTo.secondName}`
          : <span className="unassigned">Unassigned</span>;
      case 'ticketStatus':
        return (
          <span className={`status-badge status-${ticket.ticketStatus?.toLowerCase()}`}>
            {STATUS_LABELS[ticket.ticketStatus] || ticket.ticketStatus?.replace('_', ' ')}
          </span>
        );
      case 'idCardStatus':
        return ticket.requestType === 'ID_CARD_REQUEST' && ticket.idCardStatus ? (
          <span className={`id-card-badge id-card-badge-${ticket.idCardStatus?.toLowerCase()}`}>
            {ticket.idCardStatus?.replace('_', ' ')}
          </span>
        ) : '-';
      case 'employeeId':
        return ticket.employeeId || '-';
      case 'employeeName':
        return <span className="employee-name">{ticket.employeeName || '-'}</span>;
      case 'employeeLocation':
        return ticket.employeeLocation || '-';
      case 'workLocation':
        return ticket.workLocation?.locationName || ticket.employeeLocation || '-';
      case 'bloodGroup':
        return ticket.bloodGroup || '-';
      case 'contactNumber':
        return ticket.contactNumber || '-';
      case 'emergencyContact':
        return ticket.emergencyContact || '-';
      case 'hasPhoto':
        return ticket.photoName ? (
          <span className="photo-indicator has-photo" title="Photo uploaded"><MdPhoto /></span>
        ) : (
          <span className="photo-indicator no-photo" title="No photo">-</span>
        );
      case 'employeeDoj':
        return ticket.employeeDoj || '-';
      case 'residentialAddress':
        return <span className="address-cell" title={ticket.residentialAddress || ''}>{ticket.residentialAddress || '-'}</span>;
      default:
        return '-';
    }
  };

  const columns = getColumnsForTab(activeTab);
  const isIdCardTab = activeTab === 'id-card';

  return (
    <div className="helpdesk-reports">
      {/* Header */}
      <div className="page-header">
        <div className="header-left">
          <h2>Helpdesk Reports</h2>
          <span className="report-count">{filteredTickets.length} record{filteredTickets.length !== 1 ? 's' : ''}</span>
        </div>
        <div className="header-actions">
          {selectedIds.size > 0 && (
            <button className="btn btn-bulk-update" onClick={() => setShowBulkModal(true)} title="Bulk status update">
              <MdCheckBox /> Status Bulk Update ({selectedIds.size})
            </button>
          )}
          {isIdCardTab && selectedIds.size > 0 && (
            <button className="btn btn-reprocess" onClick={() => setShowReprocessModal(true)} title="Request photo re-upload from employees">
              <MdReplay /> Reprocess ({selectedIds.size})
            </button>
          )}
          {isIdCardTab && (
            <button
              className="btn btn-export-images"
              onClick={exportWithImages}
              disabled={exportingImages}
              title={selectedIds.size > 0 ? `Export ${selectedIds.size} selected with images` : 'Export all with images'}
            >
              <MdImage /> {exportingImages ? 'Exporting...' : 'Export Excel with Image'}
            </button>
          )}
          <button className="btn btn-export" onClick={exportToCSV} title="Export to CSV">
            <MdFileDownload /> Export CSV
          </button>
          <button className="btn btn-secondary" onClick={() => loadReport(activeTab)} title="Refresh">
            <MdRefresh />
          </button>
        </div>
      </div>

      {/* Report Tabs */}
      <div className="report-tabs">
        {REPORT_TABS.map(tab => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.key}
              className={`report-tab ${activeTab === tab.key ? 'active' : ''}`}
              onClick={() => handleTabChange(tab.key)}
              title={tab.description}
            >
              <Icon className="tab-icon" />
              <span className="tab-label">{tab.label}</span>
              {activeTab === tab.key && tickets.length > 0 && (
                <span className="tab-count">{tickets.length}</span>
              )}
            </button>
          );
        })}
      </div>

      {/* Filter Panel (my-tickets, assigned-to-me, team tabs) */}
      {isFilterPanelTab && (
        <div className="team-filter-panel">
          <div className="team-filter-grid">
            <div className="team-filter-item">
              <label>From Date</label>
              <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} />
            </div>
            <div className="team-filter-item">
              <label>To Date</label>
              <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} />
            </div>
            <div className="team-filter-item">
              <label>Location</label>
              <select value={cityFilter} onChange={e => setCityFilter(e.target.value)}>
                <option value="ALL">-- Select --</option>
                {filterOptions.cities.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            {activeTab === 'team' && (
              <div className="team-filter-item">
                <label>HR / Assigned To</label>
                <select value={assigneeFilter} onChange={e => setAssigneeFilter(e.target.value)}>
                  <option value="ALL">-- Select --</option>
                  {filterOptions.assignees.map(a => <option key={a} value={a}>{a}</option>)}
                </select>
              </div>
            )}
            {activeTab === 'assigned-to-me' && (
              <div className="team-filter-item">
                <label>Requested By</label>
                <select value={assigneeFilter} onChange={e => setAssigneeFilter(e.target.value)}>
                  <option value="ALL">-- Select --</option>
                  {filterOptions.requesters.map(r => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>
            )}
            {activeTab === 'my-tickets' && (
              <div className="team-filter-item">
                <label>Assigned To</label>
                <select value={assigneeFilter} onChange={e => setAssigneeFilter(e.target.value)}>
                  <option value="ALL">-- Select --</option>
                  {filterOptions.assignees.map(a => <option key={a} value={a}>{a}</option>)}
                </select>
              </div>
            )}
            <div className="team-filter-item">
              <label>Category</label>
              <select value={categoryFilter} onChange={e => { setCategoryFilter(e.target.value); setSubCategoryFilter('ALL'); }}>
                <option value="ALL">-- Select --</option>
                {categories.map(c => <option key={c.id} value={c.categoryName}>{c.categoryName}</option>)}
              </select>
            </div>
            <div className="team-filter-item">
              <label>Sub Category</label>
              <select value={subCategoryFilter} onChange={e => setSubCategoryFilter(e.target.value)} disabled={categoryFilter === 'ALL'}>
                <option value="ALL">-- Select --</option>
                {subCategories.map(sc => <option key={sc.id} value={sc.subCategoryName}>{sc.subCategoryName}</option>)}
              </select>
            </div>
            <div className="team-filter-item">
              <label>Status</label>
              <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
                <option value="ALL">-- Select --</option>
                {STATUS_OPTIONS.map(s => <option key={s} value={s}>{STATUS_LABELS[s]}</option>)}
              </select>
            </div>
            <div className="team-filter-item">
              <label>Priority</label>
              <select value={priorityFilter} onChange={e => setPriorityFilter(e.target.value)}>
                <option value="ALL">-- Select --</option>
                {PRIORITY_OPTIONS.map(p => <option key={p} value={p}>{p.charAt(0) + p.slice(1).toLowerCase()}</option>)}
              </select>
            </div>
            <div className="team-filter-item">
              <label>ID Card Status</label>
              <select value={idCardStatusFilter} onChange={e => setIdCardStatusFilter(e.target.value)}>
                <option value="ALL">-- Select --</option>
                {ID_CARD_STATUS_OPTIONS.map(s => <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>)}
              </select>
            </div>
          </div>
          <div className="team-filter-actions">
            <button className="btn btn-team-display" onClick={handleFilterDisplay}>
              Display
            </button>
            {(filtersApplied || dateFrom || dateTo || statusFilter !== 'ALL' || priorityFilter !== 'ALL' || idCardStatusFilter !== 'ALL' || categoryFilter !== 'ALL' || subCategoryFilter !== 'ALL' || cityFilter !== 'ALL' || assigneeFilter !== 'ALL') && (
              <button className="btn btn-team-clear" onClick={clearAdvancedFilters}>
                <MdClear /> Clear
              </button>
            )}
          </div>
        </div>
      )}

      {/* Status Summary Cards (my-tickets, assigned-to-me, team tabs) */}
      {isFilterPanelTab && !isLoading && tickets.length > 0 && (
        <div className="team-summary-cards">
          <div className="team-summary-card team-card-overall" onClick={() => { setStatusFilter('ALL'); setFiltersApplied(true); setCurrentPage(0); }}>
            <div className="team-card-icon">
              <MdAssignment />
            </div>
            <div className="team-card-info">
              <div className="team-card-count">{filteredTickets.length}</div>
              <div className="team-card-label">Overall Requests</div>
            </div>
          </div>
          <div className="team-summary-card team-card-open" onClick={() => { setStatusFilter(statusFilter === 'OPEN' ? 'ALL' : 'OPEN'); setFiltersApplied(true); setCurrentPage(0); }}>
            <div className="team-card-icon">
              <MdOpenInNew />
            </div>
            <div className="team-card-info">
              <div className="team-card-count">{statusSummary['OPEN'] || 0}</div>
              <div className="team-card-label">Open</div>
            </div>
          </div>
          <div className="team-summary-card team-card-inprogress" onClick={() => { setStatusFilter(statusFilter === 'IN_PROGRESS' ? 'ALL' : 'IN_PROGRESS'); setFiltersApplied(true); setCurrentPage(0); }}>
            <div className="team-card-icon">
              <MdRefresh />
            </div>
            <div className="team-card-info">
              <div className="team-card-count">{(statusSummary['IN_PROGRESS'] || 0) + (statusSummary['ASSIGNED'] || 0)}</div>
              <div className="team-card-label">In Progress</div>
            </div>
          </div>
          <div className="team-summary-card team-card-reopened" onClick={() => { setStatusFilter(statusFilter === 'REOPENED' ? 'ALL' : 'REOPENED'); setFiltersApplied(true); setCurrentPage(0); }}>
            <div className="team-card-icon">
              <MdReplay />
            </div>
            <div className="team-card-info">
              <div className="team-card-count">{statusSummary['REOPENED'] || 0}</div>
              <div className="team-card-label">Hold / Reopened</div>
            </div>
          </div>
          <div className="team-summary-card team-card-closed" onClick={() => { setStatusFilter(statusFilter === 'CLOSED' ? 'ALL' : 'CLOSED'); setFiltersApplied(true); setCurrentPage(0); }}>
            <div className="team-card-icon">
              <MdCheckBox />
            </div>
            <div className="team-card-info">
              <div className="team-card-count">{(statusSummary['CLOSED'] || 0) + (statusSummary['RESOLVED'] || 0)}</div>
              <div className="team-card-label">Closed</div>
            </div>
          </div>
        </div>
      )}

      {/* ID Card Tab - Simple Summary Cards */}
      {isIdCardTab && !isLoading && tickets.length > 0 && (
        <div className="report-summary">
          <div className="summary-card summary-total">
            <div className="summary-value">{tickets.length}</div>
            <div className="summary-label">Total</div>
          </div>
          {STATUS_OPTIONS.map(status => (
            statusSummary[status] > 0 && (
              <div key={status} className={`summary-card summary-${status.toLowerCase()}`}
                onClick={() => { setStatusFilter(statusFilter === status ? 'ALL' : status); setCurrentPage(0); }}
                style={{ cursor: 'pointer' }}
              >
                <div className="summary-value">{statusSummary[status]}</div>
                <div className="summary-label">{STATUS_LABELS[status]}</div>
              </div>
            )
          ))}
        </div>
      )}

      {/* Search bar */}
      <div className="toolbar" style={{ marginTop: isFilterPanelTab ? 0 : undefined }}>
        <div className="search-box">
          <MdSearch className="search-icon" />
          <input
            type="text"
            placeholder={isIdCardTab ? 'Search by employee ID, name, location, contact...' : 'Search by ticket #, title, name, category...'}
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
          />
          {searchQuery && (
            <button className="search-clear" onClick={() => setSearchQuery('')}>
              <MdClear />
            </button>
          )}
        </div>
        {isIdCardTab && (
          <div className="toolbar-right">
            <button
              className={`btn btn-filter-toggle ${showFilters ? 'active' : ''}`}
              onClick={() => setShowFilters(!showFilters)}
            >
              <MdFilterList />
              Filters
              {showFilters ? <MdExpandLess /> : <MdExpandMore />}
            </button>
          </div>
        )}
      </div>

      {/* ID Card Filters */}
      {isIdCardTab && showFilters && (
        <div className="filter-bar">
          <div className="filter-controls">
            <div className="filter-item">
              <select value={locationFilter} onChange={e => { setLocationFilter(e.target.value); setCurrentPage(0); }}>
                <option value="ALL">All Locations</option>
                {ticketLocations.map(loc => (
                  <option key={loc} value={loc}>{loc}</option>
                ))}
              </select>
            </div>
            <div className="filter-item">
              <select value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setCurrentPage(0); }}>
                <option value="ALL">All Status</option>
                {STATUS_OPTIONS.map(s => <option key={s} value={s}>{STATUS_LABELS[s]}</option>)}
              </select>
            </div>
            <div className="filter-item">
              <select value={idCardStatusFilter} onChange={e => { setIdCardStatusFilter(e.target.value); setCurrentPage(0); }}>
                <option value="ALL">All ID Card Status</option>
                {ID_CARD_STATUS_OPTIONS.map(s => <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>)}
              </select>
            </div>
            <div className="filter-item">
              <input type="date" value={dateFrom} onChange={e => { setDateFrom(e.target.value); setCurrentPage(0); }} title="From date" />
            </div>
            <div className="filter-item">
              <input type="date" value={dateTo} onChange={e => { setDateTo(e.target.value); setCurrentPage(0); }} title="To date" />
            </div>
            {hasActiveFilters && (
              <button className="btn btn-clear-filters" onClick={clearFilters} style={{ marginLeft: 4 }}>
                <MdClear /> Clear
              </button>
            )}
          </div>
        </div>
      )}

      {/* Entries per page + pagination info */}
      {!isLoading && filteredTickets.length > 0 && (
        <div className="table-controls">
          <div className="entries-control">
            <select
              value={pageSize}
              onChange={e => { setPageSize(Number(e.target.value)); setCurrentPage(0); }}
              className="page-size-select"
            >
              {PAGE_SIZE_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            <span>entries per page</span>
          </div>
          <div className="table-search-info">
            Showing {currentPage * pageSize + 1} to {Math.min((currentPage + 1) * pageSize, filteredTickets.length)} of {filteredTickets.length} entries
            {filteredTickets.length !== tickets.length && ` (filtered from ${tickets.length} total)`}
          </div>
        </div>
      )}

      {/* Data Table */}
      <div className="data-table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th className="checkbox-th" onClick={toggleSelectAll} style={{ cursor: 'pointer', width: '36px' }}>
                {allPageSelected ? <MdCheckBox className="check-icon" /> :
                  somePageSelected ? <MdIndeterminateCheckBox className="check-icon" /> :
                    <MdCheckBoxOutlineBlank className="check-icon" />}
              </th>
              {columns.map(col => (
                <th
                  key={col.key}
                  className={col.sortable ? 'sortable-th' : ''}
                  onClick={col.sortable ? () => handleSort(col.key) : undefined}
                >
                  <span className="th-content">
                    {col.label}
                    {col.sortable && renderSortIcon(col.key)}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              Array(8).fill(null).map((_, i) => <SkeletonRow key={i} cols={columns.length + 1} />)
            ) : paginatedTickets.length === 0 ? (
              <tr>
                <td colSpan={columns.length + 1} className="empty-message">
                  {tickets.length === 0 ? 'No tickets found for this report' : 'No tickets match your filters'}
                </td>
              </tr>
            ) : (
              paginatedTickets.map((ticket, idx) => (
                <tr key={ticket.id} className={`ticket-row ${selectedIds.has(ticket.id) ? 'row-selected' : ''}`} onClick={() => navigate(`/admin/helpdesk/tickets/${ticket.id}`)}>
                  <td className="checkbox-cell" onClick={e => { e.stopPropagation(); toggleSelect(ticket.id); }}>
                    {selectedIds.has(ticket.id)
                      ? <MdCheckBox className="check-icon checked" />
                      : <MdCheckBoxOutlineBlank className="check-icon" />}
                  </td>
                  <td className="row-num">{currentPage * pageSize + idx + 1}</td>
                  <td>
                    <button
                      className="btn-icon btn-open"
                      onClick={e => { e.stopPropagation(); navigate(`/admin/helpdesk/tickets/${ticket.id}`); }}
                      title="View ticket"
                    >
                      <MdOpenInNew />
                    </button>
                  </td>
                  {columns.filter(c => c.key !== 'index' && c.key !== 'action').map(col => (
                    <td key={col.key}>{renderCellValue(ticket, col.key)}</td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {!isLoading && filteredTickets.length > 0 && (
        <div className="pagination-bar">
          <div className="pagination-info">
            Showing {currentPage * pageSize + 1}–{Math.min((currentPage + 1) * pageSize, filteredTickets.length)} of {filteredTickets.length}
          </div>
          <div className="pagination-controls">
            <button className="btn-page" disabled={currentPage <= 0} onClick={() => setCurrentPage(0)}>
              First
            </button>
            <button className="btn-page" disabled={currentPage <= 0} onClick={() => setCurrentPage(p => p - 1)}>
              <MdChevronLeft />
            </button>
            <span className="page-num">Page {currentPage + 1} of {totalPages}</span>
            <button className="btn-page" disabled={currentPage >= totalPages - 1} onClick={() => setCurrentPage(p => p + 1)}>
              <MdChevronRight />
            </button>
            <button className="btn-page" disabled={currentPage >= totalPages - 1} onClick={() => setCurrentPage(totalPages - 1)}>
              Last
            </button>
          </div>
        </div>
      )}

      {/* Bulk Status Update Modal */}
      {showBulkModal && (
        <div className="modal-overlay" onClick={() => setShowBulkModal(false)}>
          <div className="bulk-modal" onClick={e => e.stopPropagation()}>
            <h3>Bulk Status Update</h3>
            <p className="bulk-modal-info">
              Update status for <strong>{selectedIds.size}</strong> selected ticket{selectedIds.size !== 1 ? 's' : ''}
            </p>
            <div className="bulk-modal-field">
              <label>New Status</label>
              <select value={bulkStatus} onChange={e => setBulkStatus(e.target.value)}>
                <option value="">Select status...</option>
                {STATUS_OPTIONS.map(s => (
                  <option key={s} value={s}>{STATUS_LABELS[s]}</option>
                ))}
              </select>
            </div>
            <div className="bulk-modal-field">
              <label>Comment (optional)</label>
              <textarea
                value={bulkComment}
                onChange={e => setBulkComment(e.target.value)}
                placeholder="Add a comment for this status change..."
                rows={3}
              />
            </div>
            <div className="bulk-modal-actions">
              <button
                className="btn btn-secondary"
                onClick={() => { setShowBulkModal(false); setBulkStatus(''); setBulkComment(''); }}
                disabled={bulkUpdating}
              >
                Cancel
              </button>
              <button
                className="btn btn-bulk-confirm"
                onClick={handleBulkUpdate}
                disabled={!bulkStatus || bulkUpdating}
              >
                {bulkUpdating ? 'Updating...' : `Update ${selectedIds.size} Ticket${selectedIds.size !== 1 ? 's' : ''}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reprocess Modal */}
      {showReprocessModal && (
        <div className="modal-overlay" onClick={() => setShowReprocessModal(false)}>
          <div className="bulk-modal reprocess-modal" onClick={e => e.stopPropagation()}>
            <h3>Reprocess - Request Photo Re-upload</h3>
            <p className="bulk-modal-info">
              Send photo re-upload request to <strong>{selectedIds.size}</strong> employee{selectedIds.size !== 1 ? 's' : ''}.
              The existing photo will be cleared and the ticket status will be set to Reopened.
            </p>
            <div className="bulk-modal-field">
              <label>Reason / Instructions (required)</label>
              <textarea
                value={reprocessComment}
                onChange={e => setReprocessComment(e.target.value)}
                placeholder="e.g., Please upload a professional photo for ID card creation..."
                rows={4}
              />
            </div>
            <div className="bulk-modal-actions">
              <button
                className="btn btn-secondary"
                onClick={() => { setShowReprocessModal(false); setReprocessComment(''); }}
                disabled={reprocessing}
              >
                Cancel
              </button>
              <button
                className="btn btn-reprocess-confirm"
                onClick={handleReprocess}
                disabled={!reprocessComment.trim() || reprocessing}
              >
                {reprocessing ? 'Sending...' : `Send to ${selectedIds.size} Employee${selectedIds.size !== 1 ? 's' : ''}`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default HelpdeskReports;
